import { Refresh, Wifi } from "iconsax-react";
import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import settings from "../settings";
import { getRedirectUrl } from "../utils/device";

interface AccessSuccessProps {
  destinationUrl?: string; // URL đích mặc định
  destinationUrlTablet?: string; // URL đích cho Tablet
  destinationUrlDesktop?: string; // URL đích cho Desktop
  onRetry?: () => void; // Callback để thử lại nếu cần
  isLoading?: boolean;
  googleAnalyticsKey?: string; // UTM parameters từ ad config
}

const AccessSuccess: React.FC<AccessSuccessProps> = ({
  destinationUrl,
  destinationUrlTablet,
  destinationUrlDesktop,
  onRetry,
  isLoading,
  googleAnalyticsKey,
}) => {
  const [isSuccess, setIsSuccess] = useState(true);
  const intl = useIntl();

  // Validate và sử dụng destinationUrl theo device, fallback về settings nếu invalid
  const getValidDestinationUrl = () => {
    // Ưu tiên lấy URL theo device từ helper
    let baseUrl =
      getRedirectUrl(
        destinationUrl,
        destinationUrlTablet,
        destinationUrlDesktop,
      ) || settings.destinationUrl;

    // Đảm bảo URL hợp lệ (phải bắt đầu bằng http/https). Nếu không hợp lệ, dùng fallback từ settings
    if (
      !baseUrl ||
      !(baseUrl.startsWith("http://") || baseUrl.startsWith("https://"))
    ) {
      baseUrl = settings.destinationUrl;
    }

    // Parse UTM parameters từ google_analytics_key (format: ?utm_source=x&utm_medium=y&...)
    let utmString = "";
    if (googleAnalyticsKey) {
      // Loại bỏ dấu ? hoặc : ở đầu nếu có
      utmString = googleAnalyticsKey.replace(/^[?:]/, "");
    }

    // Nếu có UTM parameters từ config, append vào destination URL
    if (utmString) {
      const separator = baseUrl.includes("?") ? "&" : "?";
      return `${baseUrl}${separator}${utmString}`;
    }

    return baseUrl;
  };

  const validDestinationUrl = getValidDestinationUrl();

  // Hàm check Android
  const isAndroid = () => /Android/i.test(navigator.userAgent);

  useEffect(() => {
    if (!validDestinationUrl) return;

    console.log(`[Redirect] Browser will redirect to: ${validDestinationUrl}`);

    if (isAndroid()) {
      // Android: Redirect NGAY trong portal để user thấy landing page
      // Portal sẽ tự đóng sau vài giây (do Android detect Internet)
      // Nhưng user đã thấy được landing page trong thời gian đó
      window.location.replace(validDestinationUrl);
    } else {
      // Non-Android: redirect tự động sau 1s
      const timer = setTimeout(() => {
        window.location.href = validDestinationUrl;
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [validDestinationUrl]);

  return (
    <>
      {isSuccess && (
        <>
          {/* Main success card */}
          <div className="fixed inset-0 bg-gradient-to-br from-gray-50 via-green-50 to-gray-100 flex items-center justify-center z-50 p-4">
            <div className="bg-white/95 backdrop-blur-sm p-5 rounded-2xl shadow-md max-w-md w-full text-center border border-green-100 transform transition-all duration-500">
              {/* Icon Animation */}
              <div className="relative mb-5">
                <div className="relative bg-green-500 rounded-full p-4 mx-auto w-16 h-16 flex items-center justify-center">
                  <Wifi className="w-8 h-8 text-white animate-pulse" />
                </div>
              </div>

              {/* Title */}
              <h2 className="text-2xl font-bold text-gray-800 mb-5">
                {intl.formatMessage({ id: "connect.success" })}
              </h2>

              {/* Message */}
              <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-6 rounded-r-lg">
                <p className="text-gray-700 text-sm leading-relaxed">
                  {intl.formatMessage({ id: "connect.successExplain" })}
                </p>
              </div>

              {/* Retry Button */}
              {onRetry && (
                <button
                  onClick={onRetry}
                  disabled={isLoading}
                  className={`w-full px-6 py-3 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-2 ${
                    isLoading
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                      : "bg-gradient-to-r from-red-500 to-red-600 text-white hover:from-red-600 hover:to-red-700 hover:shadow-lg transform hover:scale-105 active:scale-95"
                  }`}
                >
                  {isLoading ? (
                    <>
                      <Refresh className="w-4 h-4 animate-spin" />
                      <span>Đang thử lại...</span>
                    </>
                  ) : (
                    <>
                      <Refresh className="w-4 h-4" />
                      <span>{intl.formatMessage({ id: "tryAgain" })}</span>
                    </>
                  )}
                </button>
              )}

              {/* Footer */}
              <footer className="mt-8 pt-4 border-t border-gray-200">
                <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                  <Wifi className="w-4 h-4" />
                  <span>
                    Powered by{" "}
                    <strong className="text-primary">
                      {settings.poweredBy}
                    </strong>
                  </span>
                </div>
              </footer>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default AccessSuccess;
